import { networkService as network, eventWebinars, userService, userOptionService } from '@/services';
import { getTranslations } from '@/utils';
import store from '@/store'

export const unityService = {
  unityInstance: null,
  currentUserId: null,
  currentLocale: 'en',
  currentEventId: null,
  unityObject: 'JSCommunicator',
  unityMethodText: 'FromJS_DebugLog',
  unityMethodJson: 'FromJS_Configure',
  configuration: {
    application: {
      lang: 'en',
    },
    network: {
      multiuserServerAddress: 'https://multiuser.saudivexpo.com',
      multiuserServerHub: 'ExpoHub',
      chatServerAddress: 'https://chat.saudivexpo.com',
      chatServerHub: 'Chat',
      multiuserServerUserUpdateIntervalMSec: 40,
      multiuserServerUserUpdateIntervalInIdleMSec: 1000,
      getVisitorsInfoMaxDelayMSec: 1000,
      chatServerPingIntervalMSec: 3000,
      chatUseEncryption: false,
    },
    user: {
      id: null,
      name: null,
      company: null,
      userPic: null,
      canOtherUsersStartChatWithMe: true,
      askMeToAllowChat: true,
      jobTitle: null,
      phone: null,
      email: null,
    },
    webinars: [],
  },
  setInstance(unityInstance) {
    this.unityInstance = unityInstance;
  },
  getInstance() {
    return this.unityInstance;
  },
  setCurrentUserId(value) {
    this.currentUserId = value;
  },
  getCurrentUserId() {
    return this.currentUserId;
  },
  setCurrentLocale(value) {
    this.currentLocale = value;
  },
  getCurrentLocale() {
    return this.currentLocale;
  },
  setCurrentEventId(value) {
    this.currentEventId = value;
  },
  getCurrentEventId() {
    return this.currentEventId;
  },

  setIsLoadingUnity(value) {
    store.commit('switchLoadingUnity', value, {root: true})
  },

  message(gameObject, method, param) {
    if (param === null) {
      param = '';
    }
    if (this.unityInstance !== null) {
      this.unityInstance.SendMessage(gameObject, method, param);
    } else {
      console.warn("vue-unity-webgl: you've sent a message to the Unity content, but it wasn\t instantiated yet.");
    }
  },

  sendTextMessage(message) {
    if (this.unityInstance) {
      // this.unityInstance.message(this.unityObject, this.unityMethodText, message);
      this.message(this.unityObject, this.unityMethodText, message);
    }
  },
  sendJsonMessage(message) {
    if (this.unityInstance) {
      // this.unityInstance.message(this.unityObject, this.unityMethodJson, message);
      this.message(this.unityObject, this.unityMethodJson, message);
    }
  },
  async sendConfiguration() {
    if (this.unityInstance) {
      const remoteConfiguration = await this.getConfigurationOptions();
      remoteConfiguration.map((item) => {
        this.configuration[item.key] = JSON.parse(item.value)[item.key];
      });
      this.configuration.application.lang = this.currentLocale;

      const user = await this.getCurrentUser(this.currentUserId, this.currentLocale);
      const userOptions = await this.getUserOptions(this.currentUserId);

      if (user) {
        const userTranslations = getTranslations(user, this.currentLocale);
        const companyTranslations = getTranslations(user.company, this.currentLocale);
        this.configuration.user.id = user.id;
        this.configuration.user.name = `${userTranslations.firstName} ${userTranslations.lastName}`;
        this.configuration.user.userPic = user.avatar;
        this.configuration.user.company = companyTranslations.name;
        this.configuration.user.jobTitle = companyTranslations.jobTitle;
        this.configuration.user.phone = user.phone;
        this.configuration.user.email = user.email;
      }
      if (userOptions) {
        for (const [key, value] of Object.entries(userOptions)) {
          if (!['id', 'user'].includes(key)) {
            this.configuration.user[key] = value;
          }
        }
      }
      const stands = await this.getStands({ eventId: this.currentEventId });
      if (stands) {
        const standsList = this.transformStands(stands, this.currentLocale);
        this.configuration.stands = [...standsList];
      }
      const webinars = await this.getWebinarsList({
        eventId: this.currentEventId,
      });
      if (webinars && webinars.data) {
        const webinarsList = this.transformWebinars(webinars.data, this.currentLocale);
        this.configuration.webinars = [...webinarsList];
      }

      this.message(this.unityObject, this.unityMethodJson, JSON.stringify(this.configuration));
    }
  },
  async sendConfigurationMessage({ userOptions, user, stands, webinars, locale = 'en' }) {
    const remoteConfiguration = await this.getConfigurationOptions();
    remoteConfiguration.map((item) => {
      this.configuration[item.key] = JSON.parse(item.value)[item.key];
    });
    this.configuration.application.lang = locale;
    if (user) {
      const userTranslations = getTranslations(user, locale);
      const companyTranslations = getTranslations(user.company, locale);
      this.configuration.user.id = user.id;
      this.configuration.user.name = `${userTranslations.firstName} ${userTranslations.lastName}`;
      this.configuration.user.userPic = user.avatar;
      this.configuration.user.company = companyTranslations.name;
      this.configuration.user.jobTitle = companyTranslations.jobTitle;
      this.configuration.user.phone = user.phone;
      this.configuration.user.email = user.email;
    }
    if (userOptions) {
      for (const [key, value] of Object.entries(userOptions)) {
        if (!['id', 'user'].includes(key)) {
          this.configuration.user[key] = value;
        }
      }
    }
    if (stands) {
      const standsList = this.transformStands(stands, locale);
      this.configuration.stands = [...standsList];
    }
    if (webinars && webinars.data) {
      const webinarsList = this.transformWebinars(webinars.data, locale);
      this.configuration.webinars = [...webinarsList];
    }
    if (this.unityInstance) {
      this.unityInstance.message(this.unityObject, this.unityMethodJson, JSON.stringify(this.configuration));
    }
  },

  getConfigurationOptions() {
    return new Promise((resolve, reject) => {
      network
        .get('/configuration-options/all/')
        .then((data) => {
          resolve(data.data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  getStands({ eventId }) {
    return new Promise((resolve, reject) => {
      network
        .get(`/unity/stands/${eventId}`)
        .then((data) => {
          resolve(data.data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },
  transformWebinars(webinars, locale) {
    return webinars.map((item) => {
      const translationsWebinar = getTranslations(item, locale);
      const translationsStatus = getTranslations(item.status, 'en');
      const translationsUser = getTranslations(item.user, locale);
      const obj = {
        id: item.id,
        name: translationsWebinar.name,
        description: translationsWebinar.description,
        status: translationsStatus.name,
        joinUrl: item.link,
        videoStreamUrl: item.videoStreamUrl,
        startDate: item.startDate,
        endDate: item.endDate,
        speaker: `${translationsUser.firstName} ${translationsUser.lastName}`,
        auditorium: item.auditorium.name,
      };
      return obj;
    });
  },
  transformStands(stands, locale) {
    return stands.map((item) => {
      const translationsCompany = getTranslations(item.company, locale);
      const obj = {
        id: item.id,
        companyName: translationsCompany.name,
        contacts: item.contacts,
        promoMaterials: [...item.promoMaterials],
      };
      return obj;
    });
  },
  async getWebinarsList({ eventId }) {
    try {
      return await eventWebinars.getWebinar({ eventId });
    } catch (e) {
      console.log(e);
    }
  },
  async getCurrentUser(userId, locale) {
    try {
      return await userService.getUserByLocale(userId, locale);
    } catch (e) {
      console.log(e);
    }
  },
  async getUserOptions(userId) {
    try {
      return await userOptionService.getUserOptionAll(userId);
    } catch (e) {
      console.log(e);
    }
  },
};

// {
//   "id": "f9beb23d-00f8-4bbb-932f-04ebf1e5c0f9",
//   "name": "Cool webinar",
//   "description": "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
//   "status": "active",
//   "joinUrl": "https://rb.gy/v7jliy",
//   "videoStreamUrl": "https://rb.gy/v7jliy",
//   "startDate": "2020-10-28T03:30:30.000Z",
//   "endDate": "2020-10-30T03:30:30.000Z",
//   "speaker": "Schimmel Kristoffer",
//   "auditorium": "Conference Hall"
// }
